import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Button,
  Input,
  Table,
  Row,
  Col,
  Tabs,
  Spin,
} from 'antd';
import { FormSelect, UploadWithTable, useCustomToast } from '../../../../components';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { authAtom } from '../../../../state';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

interface NewsReportViewProps {
  visible: boolean;
  onClose: () => void;
  record: any;
}

const NewsReportView: React.FC<NewsReportViewProps> = ({
  visible,
  onClose,
  record,
}) => {
  const [authData] = useAtom(authAtom);
  const [form] = Form.useForm();
  const [items, setItems] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('items');
  const { showToast } = useCustomToast();
  const [sectionals, setSectionals] = useState<
    { id: string; name: string }[]
  >([]);
  const [isOtherEnabled, setIsOtherEnabled] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await OnlineInventoryAPI.fetchParameters([
          'sectionals'
        ]);
        if (data) {
          setSectionals(data.sectionals || []);
        }
      } catch (error) {
        console.error('Error fetching parameters:', error);
      } finally {
        setLoading(false);
      }
    };

    const setData = async () => {
      form.setFieldsValue({
        section_id: authData?.user?.branch_id || '',
        responsible:
          authData?.user ?
            authData?.user?.document + ' - ' +
            authData?.user?.name + ' ' +
            authData?.user?.last_name
            : '',
        date: dayjs().format('YYYY-MM-DD'),
      });

      setItems(record);
    };

    setData();
    fetchData();
  }, [record, form]);

  const onFiles = (newFiles: any[]) => {
    setFiles(newFiles);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!record || record.length === 0) {
        showToast('error', 'Debe seleccionar al menos un ítem');
        return;
      }

      const formData = new FormData();

      formData.append('date', values.date || new Date().toISOString().split('T')[0]);
      formData.append('sectional_id', values.section_id);
      formData.append('event_type', values.event_type);
      formData.append('observations', values.observations || '');

      if (values.event_type === 'other') {
        formData.append('other_event_type', values.other || '');
      }

      record.forEach((item: any) => {
        formData.append('assets[]', String(item.id));
      });

      files.forEach((file) => {
        if (file.file instanceof File) {
          formData.append('documents[]', file.file);
        } else {
          showToast('error', 'Archivo ignorado, no es instancia de File')
        }
      });

      const response = await OnlineInventoryAPI.storeAssetEventV2(formData);
      showToast('success', response?.message || 'Novedad creada correctamente')
      onClose();
    } catch (error) {
      showToast('error', 'Error al guardar la novedad')
    }
  };

  const columns = [
    { title: 'Item', dataIndex: 'id', key: 'id' },
    {
      title: 'Código/placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Clase de activos',
      dataIndex: 'asset_class',
      key: 'asset_class',
      render: (_: any, record: any) => record.asset_class ? record.asset_class?.code + ' - ' + record.asset_class?.name : 'N/A',
    },
    {
      title: 'Estado del bien',
      dataIndex: 'good_status',
      key: 'good_status',
      render: (_: any, record: any) => record.good_status || 'N/A',
    },
  ];

  return (
    <Modal
      title={"Reporte de novedad - " + (record[0].inventory_name ?? 'Sin registro')}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={"95vw"}
    >
      {loading ? (
        <Row justify="center" align="middle" style={{ minHeight: 200 }}>
          <Spin size="large" />
        </Row>
      ) : (
        <Form
          layout="vertical"
          form={form}
          onValuesChange={() => {
            const values = form.getFieldsValue([
              'event_type',
              'other',
              'observations',
            ]);
            setIsFormValid(
              !!values.event_type &&
              (values.event_type === 'Entre otros' ? !!values.other : true)
            );
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Responsable" name="responsible">
                <Input disabled name="responsible" value={authData?.user?.name} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fecha" name="date">
                <Input disabled name="date" value="" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="section_id">
                <FormSelect
                  label="Seccional"
                  name="section_id"
                  disabled
                  value=""
                  options={sectionals}
                  onChange={() => { }}
                  onBlur={() => { }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="event_type">
                <FormSelect
                  label="Tipo de novedad"
                  name="event_type"
                  value=""
                  options={[
                    { name: 'Daño', id: 'damage' },
                    { name: 'Pérdida', id: 'loss' },
                    { name: 'Inconsistencia', id: 'inconsistency' },
                    { name: 'Mantenimiento preventivo', id: 'preventive-maintenance' },
                    { name: 'Mantenimiento correctivo', id: 'corrective-maintenance' },
                    { name: 'Entre otros', id: 'other' },
                  ]}
                  onChange={(value) => {
                    setIsOtherEnabled(value === 'other')
                    if (value !== 'other') form.setFieldsValue({ other: '' });
                  }
                  }
                  onBlur={() => { }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="¿Cuál?" name="other">
                <Input
                  disabled={!isOtherEnabled}
                  name="other"
                  value=""
                  placeholder={!isOtherEnabled ? '' : 'Ingrese'}
                  maxLength={100}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Observaciones" name="observations">
                <Input.TextArea
                  name="observations"
                  placeholder='Ingrese'
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Ítems" key="items">
              <Table
                rowKey="id"
                columns={columns}
                dataSource={items}
                pagination={false}
                style={{ marginTop: 16 }}
              />
            </TabPane>
            <TabPane tab="Documentos" key="documents">
              <Row>
                <Col xs={24}>
                  <UploadWithTable onFilesChange={onFiles} files={files} />
                </Col>
              </Row>
            </TabPane>
          </Tabs>
          <Row justify="end" gutter={16} style={{ marginTop: 16 }}>
            <Col>
              <Button onClick={onClose}>Cancelar</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={handleSave}
                disabled={!isFormValid}
              >
                Guardar
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default NewsReportView;
